/** @file Hook: takes the pending startAutorigReview request, fills the form and runs its analysis once. */
import type { Doc } from '@pose2d/format/types';
import { useEffect } from 'react';
import { useStore } from 'zustand';
import type { SelectionSlice } from '../../stores/types';
import { AUTORIG_ACTIONS } from './autorigActions.data';
import { autorigRequestStore } from './autorigRequestStore';
import type { AutorigForm, AutorigSession } from './autorigTypes';
import { defaultArgs } from './defaultArgs';
import { readyArgs } from './readyArgs';

/** What the hook needs from the panel body: the document (null until open), the selection, the session and the form setter. */
export interface AutorigRequestTarget {
  doc: Doc | null;
  selection: Pick<SelectionSlice, 'bones' | 'slots'>;
  session: Pick<AutorigSession, 'run'>;
  setForm: (form: AutorigForm) => void;
}

/**
 * Waits for a document, then consumes the request: an unknown analysis is dropped; a known one fills the form (the
 * request's args over the selection defaults) and runs at once unless a required argument is still missing.
 * The request is read from the store, not the render, so a double-invoked effect cannot run it twice.
 */
export function useAutorigRequest({ doc, selection, session, setForm }: AutorigRequestTarget): void {
  const asked = useStore(autorigRequestStore, (s) => s.request);
  useEffect(() => {
    const request = autorigRequestStore.getState().request;
    if (request === null || doc === null) return;
    autorigRequestStore.setState({ request: null });
    const action = AUTORIG_ACTIONS.find((a) => a.fn === request.fn);
    if (action === undefined) return;
    const args = { ...defaultArgs(action.fn, doc, selection), ...request.args };
    setForm({ fn: action.fn, args });
    const ready = readyArgs(action.fn, args);
    if (ready.missing.length === 0) void session.run({ fn: action.fn, args: ready.args, pivots: {} });
  }, [asked, doc, selection, session, setForm]);
}
